"use client";

import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2, User, Bot, Sparkles, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { trpc } from "@/lib/trpc/client";
import { getVisitorId } from "@/lib/visitorId";
import { useUser } from "@clerk/nextjs";

interface TravelChatProps {
  onClose?: () => void;
}

export default function TravelChat({ onClose }: TravelChatProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [visitorId, setVisitorId] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();
  const clerkUserId = user?.id;

  // Get or create visitor ID on mount
  useEffect(() => {
    setVisitorId(getVisitorId());
  }, []);

  // Mutation to save messages
  const saveMessage = trpc.chat.saveMessage.useMutation();

  const { messages, sendMessage, status } = useChat({
    transport: new DefaultChatTransport({
      api: "/api/chat",
    }),
    onFinish: async ({ message }) => {
      // Save assistant's response to database
      if (visitorId || clerkUserId) {
        const textParts = message.parts.filter((p) => p.type === "text");
        const content = textParts
          .map((p) => (p as { text: string }).text)
          .join("");

        // Only save if there's actual content
        if (content.trim()) {
          await saveMessage.mutateAsync({
            clerkUserId,
            visitorId: visitorId || undefined,
            role: "assistant",
            content,
          });
        }
      }
    },
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && status === "ready") {
      const userMessage = input;
      sendMessage({ text: userMessage });
      setInput("");

      // Save user's message to database
      if (visitorId || clerkUserId) {
        await saveMessage.mutateAsync({
          clerkUserId,
          visitorId: visitorId || undefined,
          role: "user",
          content: userMessage,
        });
      }
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (status === "ready") {
      sendMessage({ text: suggestion });

      // Save user's message to database
      if (visitorId || clerkUserId) {
        await saveMessage.mutateAsync({
          clerkUserId,
          visitorId: visitorId || undefined,
          role: "user",
          content: suggestion,
        });
      }
    }
  };

  return (
    <div className="flex flex-col h-[100dvh] md:h-[600px] max-w-4xl mx-auto bg-white md:rounded-lg shadow-[0_20px_60px_0px_rgba(0,0,0,0.3)] overflow-hidden border border-dirty-lime-200">
      {/* Header */}
      <div className="bg-dirty-lime-200 text-dirty-lime-800 px-4 md:px-6 py-3 sm:py-4 border-b border-dirty-lime-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-dirty-lime-800" />
            <h2 className="text-lg sm:text-xl font-semibold font-serif">
              {t("chat.title")}
            </h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden p-1 hover:bg-gray-900/10 rounded-full transition-colors touch-target"
              aria-label="Close chat"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          )}
        </div>
        <p className="text-xs sm:text-sm text-gray-700 mt-1">
          {t("chat.subtitle")}
        </p>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 bg-gray-50">
        {/* Welcome Message */}
        {messages.length === 0 && (
          <div className="space-y-6">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-dirty-lime-300 rounded-full flex items-center justify-center shadow-sm">
                <Bot className="w-5 h-5 text-dirty-lime-800" />
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <p className="text-gray-900">{t("chat.welcomeMessage")}</p>
                </div>
              </div>
            </div>

            {/* Suggestions */}
            <div className="ml-11 space-y-2">
              <p className="text-sm font-medium text-gray-700">
                {t("chat.suggestions.title")}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {[0, 1, 2, 3].map((index) => (
                  <button
                    key={index}
                    onClick={() =>
                      handleSuggestionClick(
                        t(`chat.suggestions.items.${index}`)
                      )
                    }
                    disabled={status !== "ready"}
                    className="text-left px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-dirty-lime-300 hover:bg-dirty-lime-50 transition-colors text-sm text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {t(`chat.suggestions.items.${index}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${
              message.role === "user" ? "flex-row-reverse" : ""
            }`}
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                message.role === "user"
                  ? "bg-dirty-lime-300"
                  : "bg-dirty-lime-300"
              }`}
            >
              {message.role === "user" ? (
                <User className="w-5 h-5 text-dirty-lime-800" />
              ) : (
                <Bot className="w-5 h-5 text-dirty-lime-800" />
              )}
            </div>
            <div className="flex-1 max-w-[85%] sm:max-w-[80%]">
              <div
                className={`rounded-lg p-4 shadow-sm ${
                  message.role === "user"
                    ? "bg-dirty-lime-100 text-gray-900 border border-dirty-lime-200"
                    : "bg-white text-gray-900 border border-gray-200"
                }`}
              >
                {message.parts.map((part, index) =>
                  part.type === "text" ? (
                    message.role === "user" ? (
                      <p key={index} className="whitespace-pre-wrap">
                        {part.text}
                      </p>
                    ) : (
                      <div
                        key={index}
                        className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-headings:text-gray-900 prose-strong:text-gray-900 prose-a:text-gray-700"
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {part.text}
                        </ReactMarkdown>
                      </div>
                    )
                  ) : null
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Thinking Indicator - only show when waiting for response */}
        {status === "submitted" && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-dirty-lime-300 rounded-full flex items-center justify-center shadow-sm">
              <Bot className="w-5 h-5 text-dirty-lime-800" />
            </div>
            <div className="flex-1">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 text-gray-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">{t("chat.thinking")}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 bg-white p-3 sm:p-4 safe-bottom shadow-inner">
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={status !== "ready"}
            placeholder={t("chat.placeholder")}
            rows={2}
            className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dirty-lime-300 focus:border-dirty-lime-300 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none text-gray-900 placeholder:text-gray-500 shadow-sm"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={status !== "ready" || !input.trim()}
            className="px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 bg-dirty-lime-200 text-gray-900 rounded-lg hover:bg-dirty-lime-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium min-w-[60px] sm:min-w-[80px] md:min-w-[100px] touch-target shadow-sm ring-1 ring-dirty-lime-200/50"
          >
            {status === "submitted" || status === "streaming" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm hidden sm:inline">...</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">{t("chat.send")}</span>
                <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
        <p className="text-xs text-gray-600 my-2 text-center">
          {t("chat.poweredBy")}
        </p>
      </div>
    </div>
  );
}
